/**
 * @license
 * Copyright 2021 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */
import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators/property.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { Virtualizer, virtualizerRef, } from './Virtualizer.js';
const defaultKeyFunction = (item) => item;
const defaultRenderItem = (item, idx) => html `${idx}: ${JSON.stringify(item, null, 2)}`;
export class LitVirtualizer extends LitElement {
    constructor() {
        super(...arguments);
        this._renderItem = (item, idx) => defaultRenderItem(item, idx + this._first);
        this._providedRenderItem = defaultRenderItem;
        this.items = [];
        this.scroller = false;
        this.keyFunction = defaultKeyFunction;
        this._first = 0;
        this._last = -1;
    }
    set renderItem(fn) {
        this._providedRenderItem = fn;
        this._renderItem = (item, idx) => fn(item, idx + this._first);
        this.requestUpdate();
    }
    get renderItem() {
        return this._providedRenderItem;
    }
    set layout(layout) {
        this._layout = layout;
        if (layout && this._virtualizer) {
            this._virtualizer.layout = layout;
        }
    }
    get layout() {
        return this[virtualizerRef].layout;
    }
    /**
     * Scroll to the specified index, placing that item at the given position
     * in the scroll view.
     */
    scrollToIndex(index, position = 'start') {
        this._virtualizer.scrollToIndex = { index, position };
    }
    updated() {
        if (this._virtualizer) {
            if (this._layout !== undefined) {
                this._virtualizer.layout = this._layout;
            }
            this._virtualizer.items = this.items;
        }
    }
    firstUpdated() {
        const layout = this._layout;
        this._virtualizer = new Virtualizer({
            hostElement: this,
            layout,
            scroller: this.scroller,
        });
        this.addEventListener('rangeChanged', (e) => {
            e.stopPropagation();
            this._first = e.first;
            this._last = e.last;
        });
        this._virtualizer.connected();
    }
    connectedCallback() {
        super.connectedCallback();
        if (this._virtualizer) {
            this._virtualizer.connected();
        }
    }
    disconnectedCallback() {
        if (this._virtualizer) {
            this._virtualizer.disconnected();
        }
        super.disconnectedCallback();
    }
    createRenderRoot() {
        return this;
    }
    render() {
        const { items, _renderItem, keyFunction } = this;
        const itemsToRender = [];
        // TODO (graynorton): Is this the best / only place to ensure
        // that _last isn't outside the current bounds of the items array?
        // Not sure we should ever arrive here with it out of bounds.
        // Repro case for original issue: https://tinyurl.com/yes8b2e6
        const lastItem = Math.min(items.length, this._last + 1);
        if (this._first >= 0 && this._last >= this._first) {
            for (let i = this._first; i < lastItem; i++) {
                itemsToRender.push(items[i]);
            }
        }
        return repeat(itemsToRender, keyFunction || defaultKeyFunction, _renderItem);
    }
}
__decorate([
    property()
], LitVirtualizer.prototype, "renderItem", null);
__decorate([
    property({ attribute: false })
], LitVirtualizer.prototype, "items", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], LitVirtualizer.prototype, "scroller", void 0);
__decorate([
    property()
], LitVirtualizer.prototype, "keyFunction", void 0);
__decorate([
    state()
], LitVirtualizer.prototype, "_first", void 0);
__decorate([
    state()
], LitVirtualizer.prototype, "_last", void 0);
__decorate([
    property({ attribute: false })
], LitVirtualizer.prototype, "layout", null);
//# sourceMappingURL=LitVirtualizer.js.map